import { ICinemaPalette, withAlpha } from '../palette';
import { getCinemaStyles } from '../styles';
import {
  BODY,
  DISP,
  getSharedTokens,
  GLASS_BORDER,
  HALO,
  INK_DIM,
  MOBILE,
  MONO,
  RISE,
  SOFT_HALO,
} from './styleTokens';

export const getOverlayStyles = (palette: ICinemaPalette) => {
  const cinema = getCinemaStyles(palette);
  const { focusRing, dot, kicker } = getSharedTokens(palette);

  return {
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: '6px',
      zIndex: 18,
      display: 'flex',
      flexDirection: 'column',
      padding: '84px 44px 22px',
      [MOBILE]: { padding: '74px 0 0' },
    },
    backdrop: {
      position: 'absolute',
      inset: 0,
      background:
        'radial-gradient(ellipse 120% 90% at 50% 0%, rgba(4,6,9,.5), rgba(4,6,9,.86) 72%)',
      animation: 'mytaMiniGameFade .25s ease-out',
    },
    panel: {
      position: 'relative',
      flex: '1 1 auto',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      borderRadius: '22px',
      border: '1px solid rgba(255,255,255,.14)',
      background: 'rgba(7,9,13,.92)',
      boxShadow:
        '0 40px 120px rgba(4,6,9,.7), 0 0 0 1px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.08)',
      animation: RISE,
      [MOBILE]: {
        borderRadius: '18px 18px 0 0',
        borderLeft: 0,
        borderRight: 0,
        borderBottom: 0,
      },
    },
    still: { animation: 'none' },
    header: {
      position: 'relative',
      flex: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      minHeight: '66px',
      padding: '10px 14px 10px 22px',
      background:
        'linear-gradient(180deg, rgba(255,255,255,.055), rgba(255,255,255,.015))',
      borderBottom: '1px solid rgba(255,255,255,.08)',
      [MOBILE]: {
        flexWrap: 'wrap',
        gap: '10px 12px',
        padding: '10px 10px 12px 16px',
      },
    },
    titleBlock: {
      flex: 'none',
      minWidth: 0,
      [MOBILE]: { flex: '1 1 auto' },
    },
    kicker: { ...kicker, marginBottom: '4px' },
    title: {
      fontFamily: DISP,
      fontSize: '18px',
      fontWeight: 600,
      letterSpacing: '-.01em',
      lineHeight: 1.15,
      color: '#fff',
      whiteSpace: 'nowrap',
      [MOBILE]: { fontSize: '16px' },
    },
    status: {
      flex: '1 1 auto',
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '14px',
      [MOBILE]: {
        order: 3,
        flexBasis: '100%',
        justifyContent: 'space-between',
      },
    },
    statusDot: {
      ...dot,
      animation: 'mytaCinemaPulse 1.7s ease-in-out infinite',
    },
    statusDotStatic: { animation: 'none' },
    statusText: {
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '3px',
      [MOBILE]: { flex: '1 1 auto', alignItems: 'flex-start' },
    },
    statusStep: {
      maxWidth: '100%',
      fontFamily: MONO,
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '.15em',
      textTransform: 'uppercase',
      color: '#fff',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    statusLine: {
      maxWidth: '46ch',
      fontFamily: BODY,
      fontSize: '12.5px',
      lineHeight: 1.35,
      color: INK_DIM,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '& b': {
        fontFamily: MONO,
        fontWeight: 600,
        color: '#fff',
        fontVariantNumeric: 'tabular-nums',
      },
      [MOBILE]: { maxWidth: '100%' },
    },
    statusPct: {
      flex: 'none',
      fontFamily: MONO,
      fontSize: '24px',
      fontWeight: 600,
      letterSpacing: '-.03em',
      lineHeight: 1,
      color: '#fff',
      fontVariantNumeric: 'tabular-nums',
      '& small': { fontSize: '13px', color: INK_DIM },
    },
    actions: {
      flex: 'none',
      display: 'flex',
      gap: '8px',
    },
    iconBtn: {
      ...cinema.sysBtn,
      width: '38px',
      height: '38px',
      '&:focus-visible': focusRing,
    },
    streak: {
      position: 'absolute',
      left: 0,
      bottom: '-1px',
      height: '2px',
      pointerEvents: 'none',
      background: `linear-gradient(90deg, ${withAlpha(
        palette.buttonBackgroundColor,
        0,
      )}, ${palette.buttonBackgroundColor} 38%, ${palette.accentHi})`,
      boxShadow: `0 0 12px ${palette.accentGlow}`,
      transition: 'width .6s cubic-bezier(.2,.7,.2,1)',
      '&::after': {
        content: '""',
        position: 'absolute',
        right: '-3px',
        top: '50%',
        width: '6px',
        height: '6px',
        marginTop: '-3px',
        borderRadius: '50%',
        background: '#fff',
        boxShadow: `0 0 0 2px ${withAlpha(
          palette.accentHi,
          0.45,
        )}, 0 0 14px 4px ${palette.accentGlow}`,
      },
    },
    viewport: {
      position: 'relative',
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
      background: '#05070a',
    },
    host: {
      position: 'absolute',
      inset: 0,
      isolation: 'isolate',
      outline: 'none',
      touchAction: 'none',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      WebkitTouchCallout: 'none',
    },
    warming: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      pointerEvents: 'none',
      background:
        'radial-gradient(ellipse 60% 50% at 50% 48%, rgba(255,255,255,.05), transparent 70%)',
    },
    warmingDots: {
      display: 'flex',
      gap: '10px',
      '& span': {
        ...dot,
        animation: 'mytaMiniGameDots 1.2s ease-in-out infinite',
      },
      '& span:nth-of-type(2)': { animationDelay: '.15s' },
      '& span:nth-of-type(3)': { animationDelay: '.3s' },
    },
    warmingTitle: {
      fontFamily: DISP,
      fontSize: '20px',
      fontWeight: 600,
      letterSpacing: '-.01em',
      color: '#fff',
      textShadow: HALO,
    },
    warmingSub: { ...kicker, letterSpacing: '.2em' },
    scrim: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      boxSizing: 'border-box',
      animation: 'mytaMiniGameFade .3s ease-out',
      [MOBILE]: { padding: '16px' },
    },
    failedScrim: {
      background: 'rgba(5,7,10,.72)',
      backdropFilter: 'blur(8px) saturate(.9)',
      WebkitBackdropFilter: 'blur(8px) saturate(.9)',
    },
    readyScrim: {
      background: `radial-gradient(ellipse 70% 60% at 50% 55%, ${withAlpha(
        palette.buttonBackgroundColor,
        0.22,
      )}, transparent 70%), rgba(4,6,9,.62)`,
      backdropFilter: 'blur(8px) saturate(.9)',
      WebkitBackdropFilter: 'blur(8px) saturate(.9)',
    },
    card: {
      position: 'relative',
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: '480px',
      padding: '32px 36px 30px',
      borderRadius: '22px',
      border: GLASS_BORDER,
      background: 'rgba(10,13,19,.9)',
      boxShadow:
        '0 30px 80px rgba(4,6,9,.6), inset 0 1px 0 rgba(255,255,255,.1)',
      textAlign: 'center',
      overflow: 'hidden',
      animation: RISE,
      '& > svg': {
        width: '30px',
        height: '30px',
        color: INK_DIM,
        marginBottom: '12px',
      },
      [MOBILE]: { padding: '26px 22px 24px' },
    },
    readyCard: {
      boxShadow: `0 30px 80px rgba(4,6,9,.6), 0 0 70px ${withAlpha(
        palette.buttonBackgroundColor,
        0.2,
      )}, inset 0 1px 0 rgba(255,255,255,.1)`,
    },
    readyKicker: {
      ...kicker,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '9px',
      color: '#fff',
      marginBottom: '12px',
      '&::before': { content: '""', ...dot },
    },
    cardTitle: {
      fontFamily: DISP,
      fontSize: '24px',
      fontWeight: 600,
      letterSpacing: '-.015em',
      lineHeight: 1.1,
      color: '#fff',
      marginBottom: '10px',
    },
    readyHeadline: {
      fontFamily: DISP,
      fontSize: '32px',
      fontWeight: 600,
      letterSpacing: '-.02em',
      lineHeight: 1.08,
      color: '#fff',
      textShadow: HALO,
      [MOBILE]: { fontSize: '26px' },
    },
    cardText: {
      fontFamily: BODY,
      fontSize: '14px',
      lineHeight: 1.55,
      color: INK_DIM,
      textShadow: SOFT_HALO,
    },
    readyClock: {
      marginTop: '12px',
      fontFamily: BODY,
      fontSize: '14px',
      color: INK_DIM,
      '& b': {
        fontFamily: MONO,
        fontWeight: 600,
        color: '#fff',
        fontVariantNumeric: 'tabular-nums',
      },
    },
    cardActions: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: '12px',
      marginTop: '24px',
    },
    cta: {
      ...cinema.cta,
      '&:focus-visible': focusRing,
    },
    headerCta: {
      ...cinema.cta,
      flex: 'none',
      padding: '10px 18px',
      fontSize: '14px',
      '&:focus-visible': focusRing,
    },
    ghostBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px 22px',
      borderRadius: '12px',
      border: '1px solid rgba(255,255,255,.22)',
      background: 'rgba(255,255,255,.06)',
      color: '#fff',
      fontFamily: DISP,
      fontSize: '15px',
      fontWeight: 600,
      letterSpacing: '.01em',
      cursor: 'pointer',
      transition: 'background .15s, border-color .15s',
      '&:hover': {
        background: 'rgba(255,255,255,.12)',
        borderColor: 'rgba(255,255,255,.36)',
      },
      '&:focus-visible': focusRing,
    },
    fuse: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '3px',
      background: 'rgba(255,255,255,.08)',
    },
    fuseFill: {
      height: '100%',
      transformOrigin: 'left center',
      background: `linear-gradient(90deg, ${palette.buttonBackgroundColor}, ${palette.accentHi})`,
      boxShadow: `0 0 10px ${palette.accentGlow}`,
      transition: 'transform .25s linear',
    },
  };
};
